import { ReactNode } from "react";
import { Box, Button } from "@mui/material";
import strings from "global/constants/StringConstants";
import { fillCopy } from "helpers/methods";
import styles from "../AiWebForm.styles";
import { ChevronDownGlyph, ChevronLeftGlyph, GlobeGlyph, HistoryGlyph, SparkleGlyph } from "./AiWebFormIcons";

const copy = strings.aiWebForm;

interface VersionButtonProps {
  version: number | null;
  isCurrent: boolean;
  open: boolean;
  disabled: boolean;
  onClick: () => void;
  /** The phone tab bar drops the Current badge. */
  showBadge: boolean;
}

/** "Version 3 · Current ▾", or "Building…" while the first version is made. */
export const AiVersionButton = ({ version, isCurrent, open, disabled, onClick, showBadge }: VersionButtonProps) => (
  <Box
    component="button"
    type="button"
    sx={styles.versionButton}
    onClick={onClick}
    disabled={disabled}
    aria-haspopup="dialog"
    aria-expanded={open}
  >
    <HistoryGlyph />
    {version != null ? fillCopy(copy.versionButton, { version }) : copy.building}
    {showBadge && version != null && isCurrent && (
      <Box component="span" sx={styles.currentBadge}>
        {copy.current}
      </Box>
    )}
    <ChevronDownGlyph />
  </Box>
);

interface Props {
  compact: boolean;
  title: string;
  templateLine: string;
  publishedVersion: number | null;
  onBack: () => void;
  onPublish: () => void;
  publishDisabled: boolean;
  /** Only on the desktop layout; the phone tab bar carries it. */
  versionButton?: ReactNode;
}

export const AiBuilderHeader = ({
  compact,
  title,
  templateLine,
  publishedVersion,
  onBack,
  onPublish,
  publishDisabled,
  versionButton,
}: Props) => (
  <Box component="header" sx={styles.header}>
    <Box component="button" type="button" sx={styles.backButton} aria-label={copy.backToWebForms} onClick={onBack}>
      <ChevronLeftGlyph />
    </Box>
    <Box sx={styles.titleBlock}>
      <Box sx={styles.titleRow}>
        <Box component="h1" sx={styles.title} title={title}>
          {title}
        </Box>
        {!compact && (
          <Box component="span" sx={styles.aiBadge}>
            <SparkleGlyph />
            {copy.aiBadge}
          </Box>
        )}
      </Box>
      <Box sx={styles.subRow}>
        {compact && (
          <Box component="span" sx={styles.aiBadge}>
            <SparkleGlyph />
            {copy.aiBadge}
          </Box>
        )}
        <Box component="span" sx={styles.templateLine} title={templateLine}>
          {templateLine}
        </Box>
      </Box>
    </Box>
    <Box sx={styles.headerSpacer} />
    <Box sx={styles.publishStatus}>
      <Box component="span" sx={styles.statusDot(publishedVersion != null)} />
      {publishedVersion != null ? fillCopy(copy.publishedStatus, { version: publishedVersion }) : copy.notPublished}
    </Box>
    {versionButton}
    <Button
      variant="contained"
      sx={styles.publishButton}
      startIcon={compact ? undefined : <GlobeGlyph />}
      onClick={onPublish}
      disabled={publishDisabled}
    >
      {copy.publish}
    </Button>
  </Box>
);
